/**
 * Cliente da API de season (§14.4, §24).
 *
 * Regra que vale para tudo aqui: **season é um bônus, nunca um pedágio.** O
 * jogo tem de funcionar inteiro sem conta, sem rede e sem servidor. Por isso
 * nenhuma falha desta camada propaga — ela devolve `null` e o jogo segue.
 *
 * A submissão manda o log de INPUTS e não confia no próprio resultado: quem
 * decide o score é a reexecução no servidor. O `claim` viaja só para o
 * servidor poder flagrar adulteração.
 */

import type { Replay } from '@pixelraid/core'

/** Mesma origem do relay: o nginx roteia /pixelraid-ws/ para o serviço. */
const BASE = '/pixelraid-ws'

export interface SeasonInfo {
  readonly season: number
  readonly startsOn: string
  readonly endsOn: string
  readonly tracks: readonly number[]
  readonly rules: number
  readonly lengthDays: number
}

export interface TopRow {
  readonly player: string
  readonly name: string
  readonly dist: number
  readonly score: number
}

export interface SubmitResult {
  readonly season: number
  readonly track: number
  readonly dist: number
  readonly score: number
  readonly improved: boolean
}

/** Motivo da recusa, quando há. `null` em sucesso. */
export type SubmitError = 'anonimo' | 'regra-mudou' | 'recusado' | 'offline' | 'fora-da-season'

async function get<T>(path: string): Promise<T | null> {
  try {
    const r = await fetch(`${BASE}${path}`, { credentials: 'same-origin' })
    if (!r.ok) return null
    return (await r.json()) as T
  } catch {
    // Servidor fora do ar não pode quebrar o menu.
    return null
  }
}

export const fetchSeason = (): Promise<SeasonInfo | null> => get<SeasonInfo>('/season')

export async function fetchTop(season: number, track: number, limit = 10): Promise<TopRow[]> {
  const r = await get<{ rows: TopRow[] }>(`/season/top?s=${season}&t=${track}&limit=${limit}`)
  return r?.rows ?? []
}

/** Envia a run. Devolve o resultado, ou o motivo da recusa. */
export async function submitRun(
  replay: Replay,
): Promise<{ ok: SubmitResult } | { erro: SubmitError }> {
  try {
    const r = await fetch(`${BASE}/season/submit`, {
      method: 'POST',
      credentials: 'same-origin',
      // Sem este header o serviço devolve 403. É a mesma trava CSRF do
      // account-service: outra origem não põe header custom sem preflight.
      headers: { 'content-type': 'application/json', 'X-PF-Request': '1' },
      body: JSON.stringify(replay),
    })
    if (r.ok) return { ok: (await r.json()) as SubmitResult }
    if (r.status === 401) return { erro: 'anonimo' }
    if (r.status === 409) return { erro: 'regra-mudou' }
    if (r.status === 400) return { erro: 'fora-da-season' }
    return { erro: 'recusado' }
  } catch {
    return { erro: 'offline' }
  }
}

/** Mensagem curta para a tela de fim de partida. Cabe em 256 px. */
export function submitMessage(r: { ok: SubmitResult } | { erro: SubmitError }): string {
  if ('ok' in r) return r.ok.improved ? 'RANKING: SUBIU!' : 'ranking: sem melhora'
  switch (r.erro) {
    case 'anonimo':
      // O jogador não perdeu nada: a run valeu, só não entrou no ranking.
      return 'entre na conta para\nvaler no ranking'
    case 'regra-mudou':
      return 'ranking: regra mudou'
    case 'fora-da-season':
      return 'ranking: fora da season'
    case 'offline':
      return 'ranking: sem conexão'
    default:
      return 'ranking: run recusada'
  }
}
